import { PERMISSIONS } from '@/client/config/permissions';
import type { AdminUserBulkAction } from '@/client/types/admin/users-orgs';

/** Mirrors ff-api `admin/model/user/user_bulk.py`. */
export const MAX_BULK_USERS = 1000;
export const MAX_EXPORT_USERS = 10000;

export const BULK_ACTION_META: Record<
  AdminUserBulkAction,
  {
    label: string;
    verb: string;
    code: string;
    destructive: boolean;
    needsRole?: boolean;
    askReason?: boolean;
    note?: string;
    /** Offers the opt-in retag of existing listings; the string is what happens to them. */
    cascadeEffect?: string;
  }
> = {
  add_role: { label: 'Add role', verb: 'add a role to', code: PERMISSIONS.user.roleAssign, destructive: false, needsRole: true },
  remove_role: { label: 'Remove role', verb: 'remove a role from', code: PERMISSIONS.user.roleRevoke, destructive: false, needsRole: true },
  suspend: {
    label: 'Suspend',
    verb: 'suspend',
    code: PERMISSIONS.user.edit,
    destructive: true,
    askReason: true,
    note: 'Suspended users cannot sign in or transact until the suspension is lifted.',
  },
  unsuspend: { label: 'Lift suspension', verb: 'lift the suspension on', code: PERMISSIONS.user.edit, destructive: false },
  delete: {
    label: 'Delete',
    verb: 'delete',
    code: PERMISSIONS.user.delete,
    destructive: true,
    askReason: true,
    note: 'A soft delete: sign-in is refused, but nothing is erased and the users can be restored. To permanently erase personal data, use Erase on a user’s page.',
  },
  restore: { label: 'Restore', verb: 'restore', code: PERMISSIONS.user.delete, destructive: false },
  mark_test: {
    label: 'Mark as test',
    verb: 'set test status on',
    code: PERMISSIONS.user.edit,
    destructive: false,
    askReason: true,
    note: 'What they post from now on is hidden from the public site. Listings they already created keep their current visibility.',
    cascadeEffect: 'hidden from the public site',
  },
  mark_live: {
    label: 'Mark as live',
    verb: 'clear test status on',
    code: PERMISSIONS.user.edit,
    destructive: false,
    askReason: true,
    note: 'What they post from now on is public and they stop seeing test content. Listings they already created stay test-only.',
    cascadeEffect: 'made visible to the public',
  },
};

const REASON_LABELS: Record<string, string> = {
  own_account: 'Your own account',
  super_admin_protected: 'Super admins (only a super admin can change them)',
  already_suspended: 'Already suspended',
  not_suspended: 'Not suspended',
  already_deleted: 'Already deleted',
  already_test: 'Already a test account',
  not_test: 'Already a live account',
  persona_requires_tester: 'Personas are always test accounts (remove the persona marker first)',
  not_deleted: 'Not deleted',
  already_has_role: 'Already had the role',
  no_such_role: 'Did not have the role',
  user_not_found: 'User not found',
  erased_irreversible: 'Erased — personal data is gone, cannot be restored',
};

export const reasonLabel = (reason: string) => REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');

/** [[reason, count], …] largest first. */
export function groupReasons(items: { reason?: string; error?: string }[]): [string, number][] {
  const counts = new Map<string, number>();
  items.forEach((i) => {
    const key = i.reason ?? i.error ?? 'unknown';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}
